import React, { useEffect } from "react";
import { graphql } from "gatsby";
import Page from "~/models/page";
import Flow from "~/models/flow";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Frame from "~/views/layout/frame";
import Header from "~/views/compositions/navigation/header";
import PageHeader from "~/views/compositions/explore/header";
import FlowCard from "~/views/components/flow/card";
import Type from "~/models/flow-type";
import Breadcrumb from "~/views/compositions/navigation/breadcrumb";
import ExploreFooter from "~/views/compositions/explore/footer";

type Props = {
  data: any;
};

const FlowTypePage = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const type = new Type(data.type as any);
  const flows = type.flows;

  page.breadcrumb[3].name = page.breadcrumb[3].name.replaceAll(
    "{type.name}",
    type.name
  );
  page.breadcrumb[3].pathname = page.breadcrumb[3].pathname.replaceAll(
    "{type.slug}",
    type.slug
  );

  useEffect(() => {
    document.getElementsByTagName("body")[0].classList.remove("preventXScroll");
  }, []);

  return (
    <Frame>
      <Header />

      <Breadcrumb
        breadcrumb={page.breadcrumb}
        className="max-w-screen-xl !px-5 sm:!px-14"
      />

      <PageHeader
        title={type.name}
        label={type.label}
        description={type.description}
        className="max-w-screen-xl !px-5 sm:!px-14"
      />

      <section className="bg-neutral-99">
        <ul className="max-w-screen-xl mx-auto px-5 sm:px-14 py-8 sm:py-12 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-y-8 sm:gap-y-12 gap-2 sm:gap-x-10">
          {flows.map((flow) => (
            <li key={flow.id}>
              <FlowCard flow={flow} />
            </li>
          ))}
        </ul>
      </section>

      <ExploreFooter />
    </Frame>
  );
};

export default FlowTypePage;

export const Head = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const type = new Type(data.type as any);

  page.title = page.title.replaceAll("{type.name}", type.name);
  page.metaTitle = page.metaTitle.replaceAll("{type.name}", type.name);
  page.description = type.description;
  page.breadcrumb[3].name = page.breadcrumb[3].name.replaceAll(
    "{type.name}",
    type.name
  );
  page.breadcrumb[3].pathname = page.breadcrumb[3].pathname.replaceAll(
    "{type.slug}",
    type.slug
  );
  page.url.pathname = page.url.pathname.replaceAll("{type.slug}", type.slug);
  const sd = structuredData(page);

  return <SEO page={page} sd={sd} noindex />;
};

export const query = graphql`
  query FlowTypePage($id: String) {
    page: pageMdx(
      filter: {
        frontmatter: {
          url: { pathname: { eq: "/explore/types/{type.slug}/" } }
        }
      }
    ) {
      frontmatter {
        id
        title
        description
        breadcrumb {
          ...UrlFields
        }
        url {
          ...UrlFields
        }
        type
        releasedAt
        updatedAt
        image {
          ...AssetFields
        }
      }
    }
    type: flowTypeYaml(id: { eq: $id }) {
      id
      yamlId
      name
      slug
      flows {
        id
        yamlId
        title
        slug
        type {
          yamlId
          slug
          name
        }
        sys {
          createdAt
        }
        posters {
          imgix {
            url
          }
        }
        service {
          yamlId
          slug
          title
          category {
            yamlId
            name
            slug
          }
          logo {
            imgix {
              url
            }
          }
        }
      }
    }
  }
`;
